/**
 * useLunaProgress — данные для 3-барного виджета Status/Rank прогресса
 * над деревом (NetworkTree).
 *
 * Поведение:
 *   1. На mount — GET /me/progress (первый снапшот, чтобы UI не
 *      моргал пустым баром пока SSE подключается).
 *   2. Открывает EventSource на /me/progress/stream?token=<jwt>:
 *      • event "snapshot" — полные данные (первое сообщение SSE)
 *      • event "changed"  — re-fetch снапшота (прогресс сдвинулся)
 *   3. На unmount закрывает EventSource.
 *
 * При смене JWT (logout/login) хук автоматически переподключается:
 * useEffect зависит от token.
 *
 * Никакого поллинга — только SSE. Если соединение оборвётся,
 * браузер EventSource сам переподключится.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  lunaApi,
  getAuthToken,
  type ProgressSnapshot,
} from '@/services/api';

interface UseLunaProgressResult {
  progress: ProgressSnapshot | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function parseSnapshot(raw: string): ProgressSnapshot | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'status' in parsed && 'rank' in parsed) {
      return parsed as ProgressSnapshot;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function useLunaProgress(): UseLunaProgressResult {
  const [progress, setProgress] = useState<ProgressSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await lunaApi.progress.snapshot();
      if (cancelledRef.current) return;
      if (res.data.success && res.data.progress) {
        setProgress(res.data.progress);
        setError(null);
      }
    } catch (e) {
      if (cancelledRef.current) return;
      setError((e as Error).message || 'progress fetch failed');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);

    // 1) Initial snapshot
    void fetchSnapshot();

    // 2) SSE — подписываемся на push-обновления
    const token = getAuthToken();
    if (!token) {
      // Нет JWT — SSE не откроем. Первый fetch уже ушёл; если /me/progress
      // отдаст 401 — юзер скорее всего ещё логинится, хук переподключится
      // сам когда token появится (token-перевыпуск тянет смену контекста).
      return () => {
        cancelledRef.current = true;
      };
    }

    let es: EventSource | null = null;
    try {
      es = new EventSource(lunaApi.progress.streamUrl(token));

      es.addEventListener('snapshot', (e: MessageEvent) => {
        if (cancelledRef.current) return;
        const snap = parseSnapshot(e.data);
        if (snap) {
          setProgress(snap);
          setLoading(false);
        }
      });

      // Debounce 600ms: одна операция (PC activate) может эмитить
      // несколько progress-changed подряд → burst re-renders → анимации
      // в дереве мерцают. Один fetch после паузы — правильнее.
      let debounceTimer: number | null = null;
      es.addEventListener('changed', () => {
        if (cancelledRef.current) return;
        if (debounceTimer !== null) window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          debounceTimer = null;
          if (cancelledRef.current) return;
          void fetchSnapshot();
        }, 600);
      });

      es.addEventListener('error', () => {
        // EventSource сам переподключится. Не шумим в UI.
      });
    } catch {
      /* SSE не поддерживается — останемся с единственным снапшотом */
    }

    return () => {
      cancelledRef.current = true;
      if (es) es.close();
    };
  }, [fetchSnapshot]);

  const refetch = useCallback(() => {
    void fetchSnapshot();
  }, [fetchSnapshot]);

  return { progress, loading, error, refetch };
}
